import type { CodeQualityLevel } from '@skystone/shared';
import { cn } from '@/lib/utils';

// Module-scoped colour map (S7721 — keep helpers out of the component body).
const CHIP_BG: Record<CodeQualityLevel, string> = {
  high: 'bg-success/15 text-success',
  medium: 'bg-primary/15 text-primary',
  low: 'bg-warning/15 text-warning',
  critical: 'bg-error/15 text-error',
};

interface IGradeAPercentChipProps {
  /** Whole-number-or-1-decimal percent. Renders "—%" when null. */
  percent: number | null;
  level: CodeQualityLevel | null;
  /** Toggle the trailing " A" suffix. Coverage cards drop the suffix because
   *  there is no grade A/B/C concept for coverage. */
  showGradeASuffix: boolean;
  /** Aria-label override; defaults to "<percent>% at grade A". */
  ariaLabel?: string;
}

/**
 * Small headline chip used by `CodeQualityTrendCard`. Visual sibling of
 * DORA's `TIER_BADGE` chip — same shape, same tinted-pill pattern, but
 * coloured by `CodeQualityLevel` and labelled with a percent value.
 */
export function GradeAPercentChip({
  percent,
  level,
  showGradeASuffix,
  ariaLabel,
}: Readonly<IGradeAPercentChipProps>) {
  if (percent === null || level === null) return null;

  const display = formatPercent(percent);
  const label = showGradeASuffix ? `${display} A` : display;
  const aria = ariaLabel ?? (showGradeASuffix ? `${display} at grade A` : `${display} coverage`);

  return (
    <span
      aria-label={aria}
      className={cn(
        'rounded px-2 py-0.5 text-base font-bold uppercase tracking-wider',
        CHIP_BG[level],
      )}
    >
      {label}
    </span>
  );
}

function formatPercent(p: number): string {
  // 1-decimal when needed (78.5%), whole when exact (80%).
  return Number.isInteger(p) ? `${p}%` : `${p.toFixed(1)}%`;
}
